import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip, Switch, Tabs, Tab } from '@mui/material';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DraftsIcon from '@mui/icons-material/Drafts';
import LinkIcon from '@mui/icons-material/Link';
import DownloadIcon from '@mui/icons-material/Download';
import service, { AppReleaseDto, AppReleaseLinkDto } from '../services/appReleaseService';
import { ConfirmDialog } from '../components/common';

import './EntityManagement.css';

// استورهای شناخته‌شده — کلید key در اپ موبایل برای آیکون/برچسب استفاده می‌شود
const STORE_PRESETS: Record<'android' | 'ios', { key: string; label: string; placeholder: string }[]> = {
  android: [
    { key: 'googleplay', label: 'گوگل پلی', placeholder: 'https://play.google.com/store/apps/details?id=...' },
    { key: 'cafebazaar', label: 'کافه بازار', placeholder: 'https://cafebazaar.ir/app/...' },
    { key: 'myket', label: 'مایکت', placeholder: 'https://myket.ir/app/...' },
    { key: 'anardoni', label: 'انارستان', placeholder: 'https://anardoni.com/...' },
    { key: 'website', label: 'وب‌سایت', placeholder: 'https://gamizo.ir/app' },
    { key: 'custom', label: 'سفارشی', placeholder: 'https://...' },
  ],
  ios: [
    { key: 'appstore', label: 'اپ استور', placeholder: 'https://apps.apple.com/app/...' },
    { key: 'sibapp', label: 'سیب‌اپ', placeholder: 'https://sibapp.com/applications/...' },
    { key: 'sibche', label: 'سیبچه', placeholder: 'https://sibche.com/applications/...' },
    { key: 'anardoni', label: 'انارستان', placeholder: 'https://anardoni.com/...' },
    { key: 'website', label: 'وب‌سایت', placeholder: 'https://gamizo.ir/app' },
    { key: 'custom', label: 'سفارشی', placeholder: 'https://...' },
  ],
};

const storeLabel = (platform: 'android' | 'ios', link: AppReleaseLinkDto) => {
  if (link.label) return link.label;
  const preset = STORE_PRESETS[platform].find(p => p.key === link.key);
  return preset?.label || link.key;
};

const empty: AppReleaseDto = {
  platform: 'android',
  versionName: '',
  minSupportedVersion: '',
  isForce: false,
  isPublished: true,
  notes: '',
  storeUrl: '',
  fallbackUrl: '',
  links: [],
  directDownloadUrl: ''
};

const AppUpdatesManagement: React.FC = () => {
  const [tab, setTab] = useState(0);
  const platform = useMemo(() => (tab === 0 ? 'android' : 'ios'), [tab]);
  const [items, setItems] = useState<AppReleaseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState<AppReleaseDto | null>(null);
  const [toDelete, setToDelete] = useState<AppReleaseDto | null>(null);
  const [form, setForm] = useState<AppReleaseDto>(empty);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await service.list(platform as any);
      setItems(list);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [platform]);

  useEffect(() => { load(); }, [load]);

  const onCreate = () => {
    setEditing(null);
    setForm({ ...empty, platform: platform as any });
    setFormError('');
    setDialogOpen(true);
  };
  const onEdit = (it: AppReleaseDto) => {
    setEditing(it);
    setForm({ ...it, links: (it.links || []).map(l => ({ ...l })) });
    setFormError('');
    setDialogOpen(true);
  };
  const closeDialog = () => { setDialogOpen(false); setEditing(null); };

  const addLink = () => setForm(f => ({
    ...f,
    links: [...(f.links || []), { key: STORE_PRESETS[f.platform][0].key, label: '', url: '' }]
  }));
  const updateLink = (idx: number, patch: Partial<AppReleaseLinkDto>) => setForm(f => ({
    ...f,
    links: (f.links || []).map((l, i) => (i === idx ? { ...l, ...patch } : l))
  }));
  const removeLink = (idx: number) => setForm(f => ({
    ...f,
    links: (f.links || []).filter((_, i) => i !== idx)
  }));

  const onSave = async () => {
    if (!form.versionName) { setFormError('لطفا نسخه را وارد کنید'); return; }
    if (!form.minSupportedVersion) { setFormError('لطفا حداقل نسخه مجاز را وارد کنید'); return; }
    const cleanedLinks = (form.links || []).filter(l => (l.url || '').trim());
    const payload: AppReleaseDto = { ...form, links: cleanedLinks };
    setSaving(true);
    try {
      if (editing?._id) await service.update(editing._id, payload);
      else await service.create(payload);
      closeDialog();
      await load();
    } catch (e: any) {
      setFormError(e.response?.data?.message || 'خطا در ذخیره');
    } finally { setSaving(false); }
  };

  const togglePublish = async (it: AppReleaseDto) => {
    try { await service.publish(it._id!, !it.isPublished); await load(); }
    catch (e) { console.error(e); }
  };

  const openDelete = (it: AppReleaseDto) => { setToDelete(it); setDeleteOpen(true); };
  const closeDelete = () => { setDeleteOpen(false); setToDelete(null); };
  const onDelete = async () => {
    if (!toDelete?._id) return;
    setSaving(true);
    try { await service.remove(toDelete._id); closeDelete(); await load(); }
    catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const publishedCount = items.filter(i => i.isPublished).length;
  const draftCount = items.filter(i => !i.isPublished).length;

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت به‌روزرسانی اپ</h1>
          <p className="entity-subtitle">مدیریت نسخه‌های اپلیکیشن اندروید و iOS</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={load} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
          <button className="header-btn primary" onClick={onCreate}>
            <AddIcon fontSize="small" />
            نسخه جدید
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><SystemUpdateIcon /></div>
          <div>
            <p className="entity-stat-label">کل نسخه‌ها ({platform})</p>
            <p className="entity-stat-value">{items.length}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">منتشر شده</p>
            <p className="entity-stat-value">{publishedCount}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><DraftsIcon /></div>
          <div>
            <p className="entity-stat-label">پیش‌نویس</p>
            <p className="entity-stat-value">{draftCount}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="entity-filters" style={{ padding: 0, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ px: 2, borderBottom: '1px solid var(--color-border)' }}
        >
          <Tab icon={<AndroidIcon />} iconPosition="start" label="Android" />
          <Tab icon={<AppleIcon />} iconPosition="start" label="iOS" />
        </Tabs>
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : items.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><SystemUpdateIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">نسخه‌ای ثبت نشده</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>نسخه</th>
                  <th>حداقل نسخه مجاز</th>
                  <th>نوع</th>
                  <th>وضعیت</th>
                  <th>لینک‌ها</th>
                  <th style={{ width: 180 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it._id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">
                          {platform === 'android' ? <AndroidIcon /> : <AppleIcon />}
                        </div>
                        <div className="entity-info">
                          <span className="entity-name">{it.versionName}</span>
                          {it.versionCode && <span className="entity-desc">Code: {it.versionCode}</span>}
                        </div>
                      </div>
                    </td>
                    <td><span className="cell-code" dir="ltr">{it.minSupportedVersion}</span></td>
                    <td>
                      <span className={`tag tag-sm ${it.isForce ? 'tag-danger' : 'tag-success'}`}>
                        {it.isForce ? 'اجباری' : 'اختیاری'}
                      </span>
                    </td>
                    <td>
                      <span className={`tag tag-sm ${it.isPublished ? 'tag-active' : 'tag-warning'}`}>
                        {it.isPublished ? 'منتشر شده' : 'پیش‌نویس'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 220 }}>
                        {(it.links || []).map((l, i) => (
                          <span key={i} className="tag tag-sm" title={l.url}>
                            <LinkIcon style={{ fontSize: 12, marginLeft: 2 }} />
                            {storeLabel(platform as any, l)}
                          </span>
                        ))}
                        {it.directDownloadUrl && (
                          <span className="tag tag-sm tag-active" title={it.directDownloadUrl}>
                            <DownloadIcon style={{ fontSize: 12, marginLeft: 2 }} />
                            دانلود مستقیم
                          </span>
                        )}
                        {!(it.links || []).length && !it.directDownloadUrl && (
                          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                            {it.storeUrl || it.fallbackUrl ? 'لینک قدیمی' : '-'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="ویرایش" arrow>
                          <button className="action-btn edit" onClick={() => onEdit(it)}>
                            <EditIcon />
                          </button>
                        </Tooltip>
                        <Tooltip title={it.isPublished ? 'عدم انتشار' : 'انتشار'} arrow>
                          <button
                            className={`action-btn toggle ${it.isPublished ? 'active' : ''}`}
                            onClick={() => togglePublish(it)}
                          >
                            {it.isPublished ? <UnpublishedIcon /> : <PublishIcon />}
                          </button>
                        </Tooltip>
                        <Tooltip title="حذف" arrow>
                          <button className="action-btn delete" onClick={() => openDelete(it)}>
                            <DeleteIcon />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {items.map((it) => (
                <div key={it._id} className={`entity-card ${!it.isPublished ? 'inactive' : ''}`}>
                  <div className="entity-card-header">
                    <div className="entity-card-image">
                      {platform === 'android' ? <AndroidIcon /> : <AppleIcon />}
                    </div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{it.versionName}</div>
                      <div className="entity-card-desc">
                        حداقل: {it.minSupportedVersion} • {it.isForce ? 'اجباری' : 'اختیاری'}
                      </div>
                    </div>
                    <div className="entity-card-status">
                      <span className={`tag tag-sm ${it.isPublished ? 'tag-active' : 'tag-warning'}`}>
                        {it.isPublished ? 'منتشر' : 'پیش‌نویس'}
                      </span>
                    </div>
                  </div>
                  {it.notes && (
                    <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                      {it.notes}
                    </div>
                  )}
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => onEdit(it)}>
                      <EditIcon fontSize="small" /> ویرایش
                    </button>
                    <button className="entity-card-btn" onClick={() => togglePublish(it)}>
                      {it.isPublished ? <UnpublishedIcon fontSize="small" /> : <PublishIcon fontSize="small" />}
                      {it.isPublished ? 'عدم انتشار' : 'انتشار'}
                    </button>
                    <button className="entity-card-btn danger" onClick={() => openDelete(it)}>
                      <DeleteIcon fontSize="small" /> حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth className="appupdate-edit-dialog">
        <div className="edit-dialog-title">
          <span>{editing ? 'ویرایش نسخه' : 'ایجاد نسخه جدید'}</span>
          <button className="details-close" onClick={closeDialog}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {formError && <div className="dialog-error">{formError}</div>}
          <div className="edit-form">
            {/* Basic */}
            <div className="edit-card">
              <div className="edit-card-header">اطلاعات نسخه</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field">
                    <label>پلتفرم</label>
                    <input type="text" value={form.platform} disabled dir="ltr" />
                  </div>
                  <div className="edit-field">
                    <label>نسخه <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={form.versionName}
                      onChange={(e) => setForm({ ...form, versionName: e.target.value })}
                      placeholder="مثلا: 1.2.0"
                      dir="ltr"
                    />
                  </div>
                  <div className="edit-field">
                    <label>حداقل نسخه مجاز <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={form.minSupportedVersion}
                      onChange={(e) => setForm({ ...form, minSupportedVersion: e.target.value })}
                      placeholder="مثلا: 1.0.0"
                      dir="ltr"
                    />
                  </div>
                  <div className="edit-field">
                    <label>Version Code</label>
                    <input
                      type="number"
                      value={form.versionCode || ''}
                      onChange={(e) => setForm({ ...form, versionCode: Number(e.target.value) || undefined })}
                      placeholder="مثلا: 120"
                      dir="ltr"
                    />
                  </div>
                  <div className="edit-field full">
                    <label>یادداشت‌های نسخه</label>
                    <textarea
                      value={form.notes || ''}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="تغییرات و قابلیت‌های جدید..."
                      rows={4}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Store links */}
            <div className="edit-card">
              <div className="edit-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>لینک‌های استور</span>
                <button
                  type="button"
                  className="header-btn"
                  style={{ padding: '4px 10px', fontSize: 12 }}
                  onClick={addLink}
                >
                  <AddIcon style={{ fontSize: 16 }} /> افزودن استور
                </button>
              </div>
              <div className="edit-card-body">
                {(form.links || []).length === 0 && (
                  <div style={{ fontSize: 12.5, color: 'var(--color-text-secondary)', padding: '4px 0 10px' }}>
                    برای هر استور (گوگل پلی، کافه بازار، مایکت و...) یک لینک جدا اضافه کنید — در مودال آپدیت اپ به کاربر نمایش داده می‌شوند.
                  </div>
                )}
                {(form.links || []).map((link, idx) => {
                  const preset = STORE_PRESETS[form.platform].find(p => p.key === link.key);
                  return (
                    <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                      <select
                        value={link.key}
                        onChange={(e) => updateLink(idx, { key: e.target.value })}
                        style={{ minWidth: 130, height: 38, borderRadius: 8, border: '1px solid var(--color-border)', padding: '0 8px', background: '#fff', fontSize: 13 }}
                      >
                        {STORE_PRESETS[form.platform].map(p => (
                          <option key={p.key} value={p.key}>{p.label}</option>
                        ))}
                      </select>
                      {link.key === 'custom' && (
                        <input
                          type="text"
                          value={link.label || ''}
                          onChange={(e) => updateLink(idx, { label: e.target.value })}
                          placeholder="نام استور"
                          style={{ width: 120, height: 38, borderRadius: 8, border: '1px solid var(--color-border)', padding: '0 8px', fontSize: 13 }}
                        />
                      )}
                      <input
                        type="text"
                        value={link.url}
                        onChange={(e) => updateLink(idx, { url: e.target.value })}
                        placeholder={preset?.placeholder || 'https://...'}
                        dir="ltr"
                        style={{ flex: 1, height: 38, borderRadius: 8, border: '1px solid var(--color-border)', padding: '0 10px', fontSize: 13 }}
                      />
                      <Tooltip title="حذف لینک" arrow>
                        <button type="button" className="action-btn delete" onClick={() => removeLink(idx)} style={{ marginTop: 3 }}>
                          <DeleteIcon />
                        </button>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Direct download (in-app) */}
            {form.platform === 'android' && (
              <div className="edit-card">
                <div className="edit-card-header">دانلود مستقیم داخل اپ (APK)</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>لینک مستقیم فایل APK</label>
                      <input
                        type="text"
                        value={form.directDownloadUrl || ''}
                        onChange={(e) => setForm({ ...form, directDownloadUrl: e.target.value })}
                        placeholder="https://cdn.gamizo.ir/app/casmashop-1.2.0.apk"
                        dir="ltr"
                      />
                      <span style={{ fontSize: 11.5, color: 'var(--color-text-secondary)', marginTop: 4, display: 'block' }}>
                        اگر پر شود، گزینه «دانلود مستقیم» با نوار پیشرفت داخل اپ نمایش داده می‌شود و بعد از دانلود، نصب‌کننده اندروید باز می‌شود.
                      </span>
                    </div>
                    <div className="edit-field">
                      <label>حجم فایل (مگابایت)</label>
                      <input
                        type="number"
                        value={form.fileSize ? Math.round((form.fileSize / 1048576) * 10) / 10 : ''}
                        onChange={(e) => {
                          const mb = Number(e.target.value);
                          setForm({ ...form, fileSize: mb > 0 ? Math.round(mb * 1048576) : undefined });
                        }}
                        placeholder="مثلا: 25"
                        dir="ltr"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Legacy URLs */}
            <div className="edit-card">
              <div className="edit-card-header">لینک‌های قدیمی (سازگاری با نسخه‌های قبلی اپ)</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>Store URL</label>
                    <input
                      type="text"
                      value={form.storeUrl || ''}
                      onChange={(e) => setForm({ ...form, storeUrl: e.target.value })}
                      placeholder="https://play.google.com/..."
                      dir="ltr"
                    />
                  </div>
                  <div className="edit-field full">
                    <label>Fallback URL</label>
                    <input
                      type="text"
                      value={form.fallbackUrl || ''}
                      onChange={(e) => setForm({ ...form, fallbackUrl: e.target.value })}
                      placeholder="https://..."
                      dir="ltr"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Settings */}
            <div className="edit-card">
              <div className="edit-card-header">تنظیمات</div>
              <div className="edit-card-body">
                <div className="edit-switches">
                  <label className="edit-switch-item">
                    <div>
                      <div className="edit-switch-label">به‌روزرسانی اجباری</div>
                      <div className="edit-switch-desc">کاربر مجبور به آپدیت برای ادامه استفاده است</div>
                    </div>
                    <Switch size="small" checked={!!form.isForce}
                      onChange={(e) => setForm({ ...form, isForce: e.target.checked })} />
                  </label>
                  <label className="edit-switch-item">
                    <div>
                      <div className="edit-switch-label">منتشر شده</div>
                      <div className="edit-switch-desc">نسخه در سرور فعال و در دسترس کاربران قرار گیرد</div>
                    </div>
                    <Switch size="small" checked={!!form.isPublished}
                      onChange={(e) => setForm({ ...form, isPublished: e.target.checked })} />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={closeDialog}>انصراف</button>
          <button className="edit-btn submit" onClick={onSave} disabled={saving}>
            {saving && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {editing ? 'ذخیره تغییرات' : 'ایجاد نسخه'}
          </button>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <ConfirmDialog
        open={deleteOpen}
        title="حذف نسخه"
        message={
          <span>
            آیا از حذف نسخه <strong>«{toDelete?.versionName}»</strong> اطمینان دارید؟
            <br />این عملیات غیرقابل بازگشت است.
          </span>
        }
        cancelText="انصراف"
        confirmText="حذف نسخه"
        confirmColor="error"
        loading={saving}
        onCancel={closeDelete}
        onConfirm={onDelete}
      />
    </div>
  );
};

export default AppUpdatesManagement;
